import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Search, 
  Shield, 
  Clock, 
  CheckCircle2,
  ArrowRight,
  Building2,
  FileCheck,
  Send,
  AlertTriangle,
  TrendingUp,
  Filter,
  MapPin
} from 'lucide-react';
import { PortalLayout } from './PortalLayout';
import { useExchangeAuth, exchangeApi } from '../../contexts/ExchangeAuthContext';
import { toast } from 'sonner';

const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
  <div className="glass-card p-6">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-slate-400">{title}</p>
        <p className={`text-3xl font-heading font-bold text-${color} mt-1`}>{value}</p>
        {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
      </div>
      <div className={`w-12 h-12 rounded-xl bg-${color}/10 flex items-center justify-center`}>
        <Icon className={`w-6 h-6 text-${color}`} />
      </div>
    </div>
  </div>
);

export const BrokerDashboard = () => {
  const { user, organization } = useExchangeAuth();
  const [outgoingRequests, setOutgoingRequests] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchState, setSearchState] = useState('');
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const reqsRes = await exchangeApi.getOutgoingRequests();
      setOutgoingRequests(reqsRes.data.requests || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchState) {
      toast.error('Please select a state');
      return;
    }
    
    setSearching(true);
    try {
      const res = await exchangeApi.searchOrganizations({ 
        state: searchState, 
        type: 'provider',
        verified_only: true 
      });
      setSearchResults(res.data.organizations || []);
      if (res.data.organizations?.length === 0) {
        toast.info('No verified providers found in this state');
      }
    } catch (error) {
      console.error('Search failed:', error);
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  // Calculate stats
  const pendingRequests = outgoingRequests.filter(r => r.status === 'pending').length;
  const approvedRequests = outgoingRequests.filter(r => r.status === 'approved' || r.status === 'auto_approved' || r.status === 'downloaded').length;
  const verifiedProviders = new Set(
    outgoingRequests
      .filter(r => r.status === 'approved' || r.status === 'auto_approved' || r.status === 'downloaded')
      .map(r => r.target_id)
  ).size;

  const states = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'
  ];

  return (
    <PortalLayout role="broker">
      <div className="space-y-6">
        {/* Welcome */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold text-white">
              {organization?.legal_name || 'Broker Dashboard'}
            </h1>
            <p className="text-slate-400">Search and verify transportation providers</p>
          </div>
          <Link to="/portal/broker/search" className="btn-primary">
            <Search className="w-4 h-4 mr-2" />
            Search Providers
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Verified Providers"
            value={verifiedProviders}
            icon={CheckCircle2}
            color="neon-green"
            subtitle="Credentials verified"
          />
          <StatCard
            title="Pending Requests"
            value={pendingRequests}
            icon={Clock}
            color={pendingRequests > 0 ? 'yellow' : 'slate'}
            subtitle="Awaiting response"
          />
          <StatCard
            title="Total Requests"
            value={outgoingRequests.length}
            icon={Send}
            color="neon-purple"
          />
          <StatCard
            title="Approval Rate"
            value={outgoingRequests.length > 0 
              ? `${Math.round((approvedRequests / outgoingRequests.length) * 100)}%` 
              : '0%'}
            icon={TrendingUp}
            color="neon-cyan"
          />
        </div>

        {/* Quick Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-6 border-neon-purple/30"
        >
          <h3 className="text-lg font-semibold text-white mb-4">Quick Provider Search</h3>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm text-slate-400 mb-2">State</label>
              <select
                value={searchState}
                onChange={(e) => setSearchState(e.target.value)}
                className="w-full input-dark"
              >
                <option value="">Select a state...</option>
                {states.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={handleSearch}
                disabled={searching || !searchState}
                className="btn-primary w-full md:w-auto"
              >
                {searching ? 'Searching...' : 'Search Providers'}
              </button>
            </div>
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm text-slate-400 mb-3">
                {searchResults.length} verified provider{searchResults.length !== 1 ? 's' : ''} found
              </h4>
              <div className="grid md:grid-cols-2 gap-4">
                {searchResults.slice(0, 4).map((provider) => (
                  <div key={provider.id} className="p-4 bg-white/5 rounded-lg">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-white font-medium">{provider.legal_name}</p>
                        <p className="text-xs text-slate-500 flex items-center">
                          <MapPin className="w-3 h-3 mr-1" />
                          {provider.address_city}, {provider.address_state}
                        </p>
                      </div>
                      {provider.verified && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-neon-green/20 text-neon-green">
                          Verified
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-400">
                        {provider.fleet_size ? `${provider.fleet_size} vehicles` : 'Fleet size N/A'}
                      </span>
                      <Link
                        to={`/portal/broker/search?provider=${provider.id}`}
                        className="text-xs text-neon-purple hover:underline"
                      >
                        Request Credentials →
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
              {searchResults.length > 4 && (
                <Link
                  to={`/portal/broker/search?state=${searchState}`}
                  className="text-sm text-neon-purple hover:underline flex items-center mt-4"
                >
                  View all {searchResults.length} providers <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              )}
            </div>
          )}
        </motion.div>

        {/* Recent Requests */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Pending */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-card p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Pending Requests</h3>
              <span className={`text-xs px-2 py-1 rounded-full ${
                pendingRequests > 0 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-white/10 text-slate-400'
              }`}>
                {pendingRequests}
              </span>
            </div>
            {pendingRequests > 0 ? (
              <div className="space-y-3">
                {outgoingRequests.filter(r => r.status === 'pending').slice(0, 4).map((req) => (
                  <div key={req.id} className="p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm text-white font-medium">{req.target_name}</p>
                      <span className="text-xs text-slate-500">
                        {new Date(req.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400">
                      {req.credential_types?.length || 0} credential(s) requested
                    </p>
                  </div>
                ))}
                <Link
                  to="/portal/broker/requests"
                  className="text-sm text-neon-purple hover:underline flex items-center"
                >
                  View all <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </div>
            ) : (
              <div className="text-center py-6">
                <Clock className="w-10 h-10 text-slate-600 mx-auto mb-2" />
                <p className="text-sm text-slate-400">No pending requests</p>
              </div>
            )}
          </motion.div>

          {/* Recently Verified */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass-card p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Recently Verified</h3>
              <Link to="/portal/broker/verified" className="text-xs text-neon-green hover:underline">
                View all
              </Link>
            </div>
            {approvedRequests > 0 ? (
              <div className="space-y-3">
                {outgoingRequests
                  .filter(r => ['approved', 'auto_approved', 'downloaded'].includes(r.status))
                  .slice(0, 4)
                  .map((req) => (
                    <div key={req.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 rounded-lg bg-neon-green/20 flex items-center justify-center">
                          <CheckCircle2 className="w-4 h-4 text-neon-green" />
                        </div>
                        <div>
                          <p className="text-sm text-white">{req.target_name}</p>
                          <p className="text-xs text-slate-500">
                            {req.approved_credential_ids?.length || 0} credentials
                          </p>
                        </div>
                      </div>
                      <Link
                        to={`/portal/broker/requests/${req.id}`}
                        className="text-xs text-neon-green hover:underline"
                      >
                        View
                      </Link>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <Shield className="w-10 h-10 text-slate-600 mx-auto mb-2" />
                <p className="text-sm text-slate-400">No verified providers yet</p>
                <Link to="/portal/broker/search" className="text-xs text-neon-purple hover:underline mt-2 inline-block">
                  Search for providers
                </Link>
              </div>
            )}
          </motion.div>
        </div>

        {/* State Requirements Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="glass-card p-6 border-neon-cyan/20"
        >
          <div className="flex items-start space-x-4">
            <div className="w-10 h-10 rounded-lg bg-neon-cyan/20 flex items-center justify-center flex-shrink-0">
              <FileCheck className="w-5 h-5 text-neon-cyan" />
            </div>
            <div>
              <h4 className="text-white font-medium mb-1">State-Specific Requirements</h4>
              <p className="text-sm text-slate-400 mb-3">
                Credential requirements vary by state. Some states require DOT authority while others don't. 
                Our system automatically determines the required credentials based on the provider's operating states.
              </p>
              <Link to="/portal/broker/reports" className="text-neon-cyan text-sm hover:underline">
                View compliance reports →
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </PortalLayout>
  );
};

export default BrokerDashboard;
